export interface HelperParam {
    name: string
    label: string
    type: 'variable' | 'string' | 'number' | 'enum'
    required: boolean
    default?: string | number
    options?: string[]
    description?: string
}

export interface HelperDefinition {
    name: string
    label: string
    description: string
    category: 'text' | 'number' | 'date' | 'list' | 'url'
    params: HelperParam[]
}

export const helperRegistry: HelperDefinition[] = [

    // Text helpers
    {
        name: 'capitalize',
        label: 'Capitalize',
        description: 'Make the first letter uppercase',
        category: 'text',
        params: [
            { name: 'value', label: 'Value', type: 'variable', required: true },
        ],
    },
    {
        name: 'capitalizeAll',
        label: 'Capitalize All',
        description: 'Capitalize every word',
        category: 'text',
        params: [
            { name: 'value', label: 'Value', type: 'variable', required: true },
        ],
    },
    {
        name: 'lowercase',
        label: 'Lowercase',
        description: 'Convert to lowercase',
        category: 'text',
        params: [
            { name: 'value', label: 'Value', type: 'variable', required: true },
        ],
    },
    {
        name: 'titleize',
        label: 'Title Case',
        description: 'Convert to title case',
        category: 'text',
        params: [
            { name: 'value', label: 'Value', type: 'variable', required: true },
        ],
    },
    {
        name: 'truncate',
        label: 'Truncate',
        description: 'Shorten text to a character limit',
        category: 'text',
        params: [
            { name: 'value', label: 'Value', type: 'variable', required: true },
            { name: 'limit', label: 'Character Limit', type: 'number', required: true },
            { name: 'suffix', label: 'Suffix', type: 'string', required: false, default: '' },
        ],
    },
    {
        name: 'ellipsis',
        label: 'Ellipsis',
        description: 'Shorten text and add \u2026',
        category: 'text',
        params: [
            { name: 'value', label: 'Value', type: 'variable', required: true },
            { name: 'limit', label: 'Character Limit', type: 'number', required: true },
        ],
    },
    {
        name: 'truncateWords',
        label: 'Truncate Words',
        description: 'Shorten text to a word count',
        category: 'text',
        params: [
            { name: 'value', label: 'Value', type: 'variable', required: true },
            { name: 'count', label: 'Word Count', type: 'number', required: true },
            { name: 'suffix', label: 'Suffix', type: 'string', required: false, default: '\u2026' },
        ],
    },
    {
        name: 'replace',
        label: 'Replace',
        description: 'Replace all occurrences of text',
        category: 'text',
        params: [
            { name: 'value', label: 'Value', type: 'variable', required: true },
            { name: 'find', label: 'Find', type: 'string', required: true },
            { name: 'replacement', label: 'Replace With', type: 'string', required: true },
        ],
    },
    {
        name: 'trim',
        label: 'Trim',
        description: 'Remove leading and trailing spaces',
        category: 'text',
        params: [
            { name: 'value', label: 'Value', type: 'variable', required: true },
        ],
    },
    {
        name: 'append',
        label: 'Append',
        description: 'Add text to the end',
        category: 'text',
        params: [
            { name: 'value', label: 'Value', type: 'variable', required: true },
            { name: 'suffix', label: 'Text to Append', type: 'string', required: true },
        ],
    },
    {
        name: 'prepend',
        label: 'Prepend',
        description: 'Add text to the beginning',
        category: 'text',
        params: [
            { name: 'value', label: 'Value', type: 'variable', required: true },
            { name: 'prefix', label: 'Text to Prepend', type: 'string', required: true },
        ],
    },
    {
        name: 'reverse',
        label: 'Reverse',
        description: 'Reverse the characters',
        category: 'text',
        params: [
            { name: 'value', label: 'Value', type: 'variable', required: true },
        ],
    },
    {
        name: 'snakecase',
        label: 'Snake Case',
        description: 'Convert to snake_case',
        category: 'text',
        params: [
            { name: 'value', label: 'Value', type: 'variable', required: true },
        ],
    },
    {
        name: 'camelcase',
        label: 'Camel Case',
        description: 'Convert to camelCase',
        category: 'text',
        params: [
            { name: 'value', label: 'Value', type: 'variable', required: true },
        ],
    },
    {
        name: 'baseLocale',
        label: 'Base Locale',
        description: 'Get the base language from a locale (e.g. en-US → en)',
        category: 'text',
        params: [
            { name: 'locale', label: 'Locale', type: 'variable', required: true },
        ],
    },

    // Number helpers
    {
        name: 'add',
        label: 'Add',
        description: 'Add two numbers together',
        category: 'number',
        params: [
            { name: 'a', label: 'First Value', type: 'variable', required: true },
            { name: 'b', label: 'Second Value', type: 'variable', required: true },
        ],
    },
    {
        name: 'subtract',
        label: 'Subtract',
        description: 'Subtract one number from another',
        category: 'number',
        params: [
            { name: 'a', label: 'First Value', type: 'variable', required: true },
            { name: 'b', label: 'Second Value', type: 'variable', required: true },
        ],
    },
    {
        name: 'multiply',
        label: 'Multiply',
        description: 'Multiply two numbers',
        category: 'number',
        params: [
            { name: 'a', label: 'First Value', type: 'variable', required: true },
            { name: 'b', label: 'Second Value', type: 'variable', required: true },
        ],
    },
    {
        name: 'divide',
        label: 'Divide',
        description: 'Divide one number by another',
        category: 'number',
        params: [
            { name: 'a', label: 'First Value', type: 'variable', required: true },
            { name: 'b', label: 'Second Value', type: 'variable', required: true },
        ],
    },
    {
        name: 'abs',
        label: 'Absolute Value',
        description: 'Get the absolute value of a number',
        category: 'number',
        params: [
            { name: 'value', label: 'Value', type: 'variable', required: true },
        ],
    },
    {
        name: 'ceil',
        label: 'Round Up',
        description: 'Round a number up to the nearest whole number',
        category: 'number',
        params: [
            { name: 'value', label: 'Value', type: 'variable', required: true },
        ],
    },
    {
        name: 'floor',
        label: 'Round Down',
        description: 'Round a number down to the nearest whole number',
        category: 'number',
        params: [
            { name: 'value', label: 'Value', type: 'variable', required: true },
        ],
    },
    {
        name: 'round',
        label: 'Round',
        description: 'Round a number to the nearest whole number',
        category: 'number',
        params: [
            { name: 'value', label: 'Value', type: 'variable', required: true },
        ],
    },
    {
        name: 'remainder',
        label: 'Remainder',
        description: 'Get the remainder after dividing two numbers',
        category: 'number',
        params: [
            { name: 'a', label: 'First Value', type: 'variable', required: true },
            { name: 'b', label: 'Second Value', type: 'variable', required: true },
        ],
    },
    {
        name: 'humanize',
        label: 'Humanize Number',
        description: 'Format a number in compact form (e.g. 1.5K)',
        category: 'number',
        params: [
            { name: 'value', label: 'Value', type: 'variable', required: true },
            { name: 'locale', label: 'Locale', type: 'string', required: false, default: 'en-US' },
        ],
    },
    {
        name: 'numberFormat',
        label: 'Format Number',
        description: 'Format a number with locale and style',
        category: 'number',
        params: [
            { name: 'value', label: 'Value', type: 'variable', required: true },
            { name: 'locale', label: 'Locale', type: 'string', required: false, default: 'en-US' },
            { name: 'style', label: 'Style', type: 'enum', required: false, options: ['currency', 'percent'] },
            { name: 'currency', label: 'Currency Code', type: 'string', required: false, default: 'USD', description: 'e.g. USD, EUR, GBP' },
        ],
    },
    {
        name: 'random',
        label: 'Random Number',
        description: 'Generate a random number in a range',
        category: 'number',
        params: [
            { name: 'min', label: 'Minimum', type: 'number', required: true },
            { name: 'max', label: 'Maximum', type: 'number', required: true },
        ],
    },

    // Date helpers
    {
        name: 'now',
        label: 'Current Date',
        description: 'Get the current date and time',
        category: 'date',
        params: [
            { name: 'format', label: 'Format', type: 'string', required: false, default: 'yyyy-MM-dd HH:mm:ss', description: 'e.g. MMM dd, yyyy' },
            { name: 'timezone', label: 'Timezone', type: 'string', required: false, default: 'UTC' },
        ],
    },
    {
        name: 'dateFormat',
        label: 'Format Date',
        description: 'Format a date into a readable string',
        category: 'date',
        params: [
            { name: 'date', label: 'Date', type: 'variable', required: true },
            { name: 'format', label: 'Format', type: 'string', required: true, default: 'MMM dd, yyyy', description: 'e.g. yyyy-MM-dd, MMM dd' },
            { name: 'timezone', label: 'Timezone', type: 'string', required: false, default: 'UTC' },
        ],
    },
    {
        name: 'addDate',
        label: 'Add to Date',
        description: 'Add time to a date',
        category: 'date',
        params: [
            { name: 'date', label: 'Date', type: 'variable', required: true },
            { name: 'amount', label: 'Amount', type: 'number', required: true },
            { name: 'unit', label: 'Unit', type: 'enum', required: true, options: ['years', 'months', 'weeks', 'days', 'hours', 'minutes', 'seconds'] },
        ],
    },
    {
        name: 'subtractDate',
        label: 'Subtract from Date',
        description: 'Subtract time from a date',
        category: 'date',
        params: [
            { name: 'date', label: 'Date', type: 'variable', required: true },
            { name: 'amount', label: 'Amount', type: 'number', required: true },
            { name: 'unit', label: 'Unit', type: 'enum', required: true, options: ['years', 'months', 'weeks', 'days', 'hours', 'minutes', 'seconds'] },
        ],
    },
    {
        name: 'dateDiff',
        label: 'Date Difference',
        description: 'Get the difference between two dates',
        category: 'date',
        params: [
            { name: 'date1', label: 'First Date', type: 'variable', required: true },
            { name: 'date2', label: 'Second Date', type: 'variable', required: true },
            { name: 'unit', label: 'Unit', type: 'enum', required: false, options: ['years', 'months', 'days', 'hours', 'minutes', 'seconds'], default: 'days' },
        ],
    },
    {
        name: 'setDate',
        label: 'Set Date Part',
        description: 'Set a specific part of a date',
        category: 'date',
        params: [
            { name: 'date', label: 'Date', type: 'variable', required: true },
            { name: 'unit', label: 'Part to Set', type: 'enum', required: true, options: ['years', 'months', 'weeks', 'days', 'hours', 'minutes', 'seconds'] },
            { name: 'value', label: 'Value', type: 'number', required: true },
        ],
    },
    {
        name: 'nextDate',
        label: 'Next Day of Week',
        description: 'Get the next occurrence of a day',
        category: 'date',
        params: [
            { name: 'date', label: 'Date', type: 'variable', required: true },
            { name: 'day', label: 'Day', type: 'enum', required: true, options: ['mo', 'tu', 'we', 'th', 'fr', 'sa', 'su'] },
        ],
    },

    // List helpers
    {
        name: 'first',
        label: 'First Item',
        description: 'Get the first item from a list',
        category: 'list',
        params: [
            { name: 'array', label: 'List', type: 'variable', required: true },
        ],
    },
    {
        name: 'last',
        label: 'Last Item',
        description: 'Get the last item from a list',
        category: 'list',
        params: [
            { name: 'array', label: 'List', type: 'variable', required: true },
        ],
    },
    {
        name: 'join',
        label: 'Join List',
        description: 'Combine list items into text',
        category: 'list',
        params: [
            { name: 'array', label: 'List', type: 'variable', required: true },
            { name: 'separator', label: 'Separator', type: 'string', required: false, default: ', ' },
        ],
    },
    {
        name: 'length',
        label: 'Count',
        description: 'Get the number of items in a list',
        category: 'list',
        params: [
            { name: 'value', label: 'Value', type: 'variable', required: true },
        ],
    },
    {
        name: 'pluck',
        label: 'Pluck',
        description: 'Extract a single field from each item in a list',
        category: 'list',
        params: [
            { name: 'array', label: 'List', type: 'variable', required: true },
            { name: 'property', label: 'Field Name', type: 'string', required: true },
        ],
    },
    {
        name: 'sort',
        label: 'Sort',
        description: 'Sort a list in order',
        category: 'list',
        params: [
            { name: 'array', label: 'List', type: 'variable', required: true },
            { name: 'direction', label: 'Direction', type: 'enum', required: false, options: ['asc', 'desc'], default: 'asc' },
        ],
    },
    {
        name: 'itemAt',
        label: 'Item at Position',
        description: 'Get an item at a specific position',
        category: 'list',
        params: [
            { name: 'array', label: 'List', type: 'variable', required: true },
            { name: 'index', label: 'Position', type: 'number', required: false, default: 0 },
        ],
    },

    // URL helpers
    {
        name: 'encodeURI',
        label: 'Encode URL',
        description: 'Encode text for use in a URL',
        category: 'url',
        params: [
            { name: 'value', label: 'Value', type: 'variable', required: true },
        ],
    },
    {
        name: 'decodeURI',
        label: 'Decode URL',
        description: 'Decode URL-encoded text',
        category: 'url',
        params: [
            { name: 'value', label: 'Value', type: 'variable', required: true },
        ],
    },
]

const helperMap = new Map(helperRegistry.map(h => [h.name, h]))

export const getHelper = (name: string): HelperDefinition | undefined => {
    return helperMap.get(name)
}

export const categoryLabels: Record<HelperDefinition['category'], string> = {
    text: 'Text',
    number: 'Number',
    date: 'Date',
    list: 'List',
    url: 'URL',
}
